import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { reservationApi } from '../../services/reservationApi';
import { Reservation } from '../../types/reservation';

interface MyReservationsProps {
  userId?: string;
}

const STATUS_LABELS = {
  active: '예약중',
  completed: '완료',
  cancelled: '취소됨',
  no_show: '미출석',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-yellow-100 text-yellow-800',
};

export const MyReservations: React.FC<MyReservationsProps> = ({
  userId = 'temp-user-id', // 임시 사용자 ID
}) => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCancelModal, setShowCancelModal] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // 예약 목록 로드
  const loadReservations = async () => {
    setLoading(true);
    try {
      const response = await reservationApi.getUserReservations(
        userId,
        page,
        10,
        selectedStatus === 'all' ? undefined : selectedStatus
      );

      setReservations(response.reservations);
      setTotalPages(response.total_pages);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : '예약 목록을 불러올 수 없습니다.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReservations();
  }, [userId, page, selectedStatus]);

  // 예약 취소
  const handleCancelReservation = async (reservationId: string) => {
    try {
      await reservationApi.cancelReservation(reservationId, cancellationReason);
      setShowCancelModal(null);
      setCancellationReason('');
      await loadReservations(); // 목록 새로고침
    } catch (error) {
      setError(
        error instanceof Error ? error.message : '예약 취소에 실패했습니다.'
      );
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 시간 포맷팅
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 예약 취소 가능 여부 확인
  const canCancelReservation = (reservation: Reservation) => {
    const now = new Date();
    const startTime = new Date(reservation.start_time);
    const hoursDiff = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    return reservation.status === 'active' && hoursDiff > 2; // 2시간 전까지만 취소 가능
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">예약 목록을 불러오는 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />내 예약 현황
        </h3>

        {/* 상태 필터 */}
        <div className="flex space-x-2">
          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">전체</option>
            <option value="active">예약중</option>
            <option value="completed">완료</option>
            <option value="cancelled">취소됨</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* 예약 목록 */}
      <div className="space-y-4">
        {reservations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {selectedStatus === 'all'
              ? '예약이 없습니다.'
              : `${STATUS_LABELS[selectedStatus as keyof typeof STATUS_LABELS]} 예약이 없습니다.`}
          </div>
        ) : (
          reservations.map((reservation) => (
            <div
              key={reservation.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[reservation.status]}`}
                  >
                    {STATUS_LABELS[reservation.status]}
                  </span>
                  <span className="text-sm text-gray-500">
                    예약 일시: {formatDate(reservation.created_at)}
                  </span>
                </div>

                {canCancelReservation(reservation) && (
                  <button
                    onClick={() => setShowCancelModal(reservation.id)}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:border-red-400 transition-colors"
                  >
                    취소
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">좌석:</span>
                  <span className="ml-1 font-medium">
                    {reservation.seat_number}번
                    {reservation.section && ` (${reservation.section})`}
                  </span>
                </div>

                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">날짜:</span>
                  <span className="ml-1 font-medium">
                    {formatDate(reservation.start_time)}
                  </span>
                </div>

                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">시간:</span>
                  <span className="ml-1 font-medium">
                    {formatTime(reservation.start_time)} -{' '}
                    {formatTime(reservation.end_time)}
                    <span className="text-gray-500 ml-1">
                      ({reservation.duration_hours}시간)
                    </span>
                  </span>
                </div>
              </div>

              {reservation.status === 'cancelled' &&
                reservation.cancellation_reason && (
                  <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                    <span className="text-gray-600">취소 사유:</span>
                    <span className="ml-2">
                      {reservation.cancellation_reason}
                    </span>
                  </div>
                )}
            </div>
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            이전
          </button>

          <span className="px-3 py-1 text-sm text-gray-600">
            {page} / {totalPages}
          </span>

          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            다음
          </button>
        </div>
      )}

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">예약 취소</h4>
              <button
                onClick={() => setShowCancelModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-600 mb-3">
                정말로 이 예약을 취소하시겠습니까?
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  취소 사유 (선택사항)
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="취소 사유를 입력해주세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(null)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                돌아가기
              </button>
              <button
                onClick={() => handleCancelReservation(showCancelModal)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
